import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@shared/services/firebase';
import { ModalWrapper } from '../ui/ModalWrapper';
import { Loader2, ArrowLeft, CheckCircle2, ChevronDown, Snowflake, Flame, CreditCard, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useData } from '../../context/DataContext';
import { logError } from '@/lib/log';

interface EligibleAccount {
    liabilityId: string;
    displayName: string;
    maskedNumber?: string;
    institution: string;
    currentBalance: number;
    creditLimit?: number;
    minimumPayment?: number;
    apr?: number;
    paymentAvailability: 'SUPPORTED' | 'FIELD_ERROR';
    needsCardNumber: boolean;
    liabilityType: string;
}

type Step = 'loading' | 'amount' | 'cardNumber' | 'confirm' | 'processing' | 'success' | 'error';

interface MiniPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPaymentComplete?: () => void;
}

export const MiniPaymentModal: React.FC<MiniPaymentModalProps> = ({ isOpen, onClose, onPaymentComplete }) => {
    const { payerInfo, unifiedLiabilities, plaidAssets } = useData();
    const [step, setStep] = useState<Step>('loading');
    const [accounts, setAccounts] = useState<EligibleAccount[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<EligibleAccount | null>(null);
    const [amount, setAmount] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [showCardNumber, setShowCardNumber] = useState(false);
    const [cardNumberSubmitting, setCardNumberSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Compute snowball (lowest balance) and avalanche (highest APR) picks
    // Only consider SUPPORTED accounts for strategy badges
    const { snowballId, avalancheId } = useMemo(() => {
        const supported = accounts.filter(a => a.paymentAvailability === 'SUPPORTED');
        if (supported.length === 0) return { snowballId: null, avalancheId: null };

        const sorted = [...supported].sort((a, b) => a.currentBalance - b.currentBalance);
        const snowball = sorted[0];

        const withApr = supported.filter(a => a.apr && a.apr > 0);
        const avalanche = withApr.length > 0
            ? withApr.reduce((max, a) => (a.apr! > max.apr! ? a : max), withApr[0])
            : null;

        return {
            snowballId: snowball?.liabilityId ?? null,
            avalancheId: avalanche && avalanche.liabilityId !== snowball?.liabilityId
                ? avalanche.liabilityId
                : null,
        };
    }, [accounts]);

    // Payer checking account balance (from plaidAssets already in DataContext)
    const payerBalance = useMemo(() => {
        if (!payerInfo.accountId) return null;
        const account = plaidAssets.find(a => a.id === payerInfo.accountId);
        return account?.balance ?? null;
    }, [payerInfo.accountId, plaidAssets]);

    // Max payment: min of debt balance, 90% of payer balance, or $10k cap
    const maxPayment = useMemo(() => {
        const caps: number[] = [10000];
        if (selectedAccount) {
            caps.push(selectedAccount.currentBalance);
        }
        if (payerBalance !== null) {
            caps.push(Math.floor(payerBalance * 0.9 * 100) / 100); // 90%, rounded down to cents
        }
        return Math.max(0, Math.min(...caps));
    }, [selectedAccount, payerBalance]);

    // Build lookup: Spinwheel liability ID → last 4 digits from unified accounts
    const maskByLiabilityId = useMemo(() => {
        const map = new Map<string, string>();
        for (const ua of unifiedLiabilities) {
            const swId = ua.sources?.spinwheel?.accountId;
            if (swId && ua.mask) {
                // Extract just the last 4 digits
                const digits = ua.mask.replace(/\D/g, '');
                if (digits.length >= 4) {
                    map.set(swId, digits.slice(-4));
                }
            }
        }
        return map;
    }, [unifiedLiabilities]);

    const getAccountMask = (account: EligibleAccount): string | undefined => {
        return maskByLiabilityId.get(account.liabilityId);
    };

    // Luhn algorithm check for credit card validation
    const passesLuhn = (number: string): boolean => {
        const digits = number.replace(/\D/g, '');
        if (digits.length < 13) return false;
        let sum = 0;
        let alternate = false;
        for (let i = digits.length - 1; i >= 0; i--) {
            let n = parseInt(digits[i], 10);
            if (alternate) {
                n *= 2;
                if (n > 9) n -= 9;
            }
            sum += n;
            alternate = !alternate;
        }
        return sum % 10 === 0;
    };

    const isAmex = (digits: string): boolean => {
        return digits.startsWith('34') || digits.startsWith('37');
    };

    // Format card number with spaces: Amex 4-6-5, others 4-4-4-4
    const formatCardDisplay = (raw: string): string => {
        const digits = raw.replace(/\D/g, '');
        if (isAmex(digits)) {
            const parts = [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)];
            return parts.filter(Boolean).join(' ');
        }
        return digits.replace(/(.{4})/g, '$1 ').trim();
    };

    const maxCardLength = isAmex(cardNumber) ? 15 : 16;

    useEffect(() => {
        if (isOpen) {
            loadEligibleAccounts();
        } else {
            setStep('loading');
            setAccounts([]);
            setSelectedAccount(null);
            setAmount('');
            setCardNumber('');
            setCardNumberSubmitting(false);
            setError(null);

            setDropdownOpen(false);
        }
    }, [isOpen]);

    const loadEligibleAccounts = async () => {
        setStep('loading');
        setError(null);
        try {
            const fn = httpsCallable(functions, 'enough_get_payment_eligible_accounts');
            const result = await fn({});
            const data = result.data as any;
            const eligible = (data.accounts || []) as EligibleAccount[];
            setAccounts(eligible);
            // Pre-select snowball account from SUPPORTED accounts
            const supported = eligible.filter(a => a.paymentAvailability === 'SUPPORTED');
            if (supported.length > 0) {
                const sorted = [...supported].sort((a, b) => a.currentBalance - b.currentBalance);
                setSelectedAccount(sorted[0]);
            } else if (eligible.length > 0) {
                setSelectedAccount(eligible[0]);
            }
            setStep('amount');
        } catch (err: any) {
            logError(err, { context: 'load_eligible_accounts', tags: { operation: 'load_eligible_accounts' } });
            setError(err.message || 'Failed to load accounts');
            setStep('error');
        }
    };

    const handleSelectAccount = (account: EligibleAccount) => {
        setSelectedAccount(account);
        setDropdownOpen(false);
    };

    const handleAmountSubmit = () => {
        if (!selectedAccount) return;

        // If this account needs a card number, go to that step first
        if (selectedAccount.needsCardNumber && selectedAccount.paymentAvailability === 'FIELD_ERROR') {
            setError(null);
            setStep('cardNumber');
            return;
        }

        const parsed = parseFloat(amount);
        if (isNaN(parsed) || parsed <= 0) {
            setError('Please enter a valid amount.');
            return;
        }
        if (parsed > selectedAccount.currentBalance) {
            setError(`Amount exceeds the statement balance of $${selectedAccount.currentBalance.toLocaleString()}.`);
            return;
        }
        if (payerBalance !== null && parsed > payerBalance * 0.9) {
            setError(`Amount exceeds 90% of your available balance ($${(payerBalance * 0.9).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). We recommend keeping a buffer to avoid overdraft fees.`);
            return;
        }
        if (parsed > 10000) {
            setError('Maximum payment is $10,000.');
            return;
        }
        setError(null);
        setStep('confirm');
    };

    const handleCardNumberSubmit = async () => {
        if (!selectedAccount) return;
        const clean = cardNumber.replace(/\D/g, '');
        if (clean.length < 13 || clean.length > 19) {
            setError('Please enter a valid card number.');
            return;
        }
        if (!passesLuhn(clean)) {
            setError('This card number doesn\u2019t appear to be valid. Please double-check and try again.');
            return;
        }

        setCardNumberSubmitting(true);
        setError(null);

        try {
            const fn = httpsCallable(functions, 'enough_update_credit_card_number');
            await fn({
                creditCardId: selectedAccount.liabilityId,
                creditCardNumber: clean,
            });

            // Update local state so the account is now treated as SUPPORTED
            setAccounts(prev => prev.map(a =>
                a.liabilityId === selectedAccount.liabilityId
                    ? { ...a, paymentAvailability: 'SUPPORTED' as const, needsCardNumber: false }
                    : a
            ));
            setSelectedAccount(prev => prev ? { ...prev, paymentAvailability: 'SUPPORTED', needsCardNumber: false } : prev);
            setCardNumber('');
            setCardNumberSubmitting(false);

            // Validate amount and proceed to confirm
            const parsed = parseFloat(amount);
            if (isNaN(parsed) || parsed <= 0) {
                setError('Please enter a valid amount.');
                setStep('amount');
                return;
            }
            if (parsed > selectedAccount.currentBalance) {
                setError(`Amount exceeds the statement balance of $${selectedAccount.currentBalance.toLocaleString()}.`);
                setStep('amount');
                return;
            }
            if (payerBalance !== null && parsed > payerBalance * 0.9) {
                setError(`Amount exceeds 90% of your available balance. We recommend keeping a buffer to avoid overdraft fees.`);
                setStep('amount');
                return;
            }
            if (parsed > 10000) {
                setError('Maximum payment is $10,000.');
                setStep('amount');
                return;
            }
            setStep('confirm');
        } catch (err: any) {
            logError(err, { context: 'update_card_number', tags: { operation: 'update_card_number' } });
            setError(err.message || 'Failed to update card number. Please try again.');
            setCardNumberSubmitting(false);
        }
    };

    const handleExecutePayment = async () => {
        if (!selectedAccount) return;

        setStep('processing');
        setError(null);

        try {
            const fn = httpsCallable(functions, 'enough_execute_mini_payment');
            const result = await fn({
                amount: parseFloat(amount),
                liabilityType: selectedAccount.liabilityType,
                liabilityId: selectedAccount.liabilityId,
                targetAccountName: selectedAccount.displayName,
            });
            setStep('success');
            onPaymentComplete?.();
        } catch (err: any) {
            logError(err, { context: 'execute_payment', tags: { operation: 'execute_mini_payment' } });
            // Insufficient funds: return to amount step with a gentle message
            if (err.code === 'functions/failed-precondition' && err.message === 'INSUFFICIENT_FUNDS') {
                setError('Your bank is showing that your funds are getting low. Let\u2019s hold off on this payment for now.');
                setStep('amount');
            } else {
                setError(err.message || 'Payment failed. Please try again.');
                setStep('error');
            }
        }
    };

    const handleClose = () => {
        onClose();
    };

    const getStrategyBadge = (account: EligibleAccount) => {
        const isSnowball = account.liabilityId === snowballId;
        const isAvalanche = account.liabilityId === avalancheId;

        if (isSnowball) {
            return (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-sky bg-sky/10 px-1.5 py-0.5 rounded-full">
                    <Snowflake className="w-2.5 h-2.5" />
                    Snowball
                </span>
            );
        }
        if (isAvalanche) {
            return (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-clay bg-clay/10 px-1.5 py-0.5 rounded-full">
                    <Flame className="w-2.5 h-2.5" />
                    Avalanche
                </span>
            );
        }
        return null;
    };

    const getAccountStatusIndicator = (account: EligibleAccount) => {
        if (account.needsCardNumber && account.paymentAvailability === 'FIELD_ERROR') {
            return (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-foliage bg-foliage/10 px-1.5 py-0.5 rounded-full">
                    <CreditCard className="w-2.5 h-2.5" />
                    Needs card #
                </span>
            );
        }
        return null;
    };

    const renderLoading = () => (
        <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-foliage mb-3" />
            <p className="text-sm text-growth/70">You're doing great...</p>
        </div>
    );

    const renderAmountInput = () => (
        <div className="space-y-4">
            {accounts.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-sm text-growth/70">No accounts are currently eligible for payment.</p>
                    <p className="text-xs text-growth/50 mt-1">
                        Accounts must have bill payment support enabled by the servicer.
                    </p>
                </div>
            ) : (
                <>
                    {/* Account Selector */}
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wider text-growth/60 mb-2">Toward</label>
                        <div className="relative">
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="w-full p-3 bg-cloud border border-mist/20 rounded-xl text-left flex items-center gap-3 hover:border-foliage/30 transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-medium text-growth truncate ph-mask">
                                            {selectedAccount?.displayName}
                                            {selectedAccount && getAccountMask(selectedAccount) && <span className="text-growth/50 font-normal"> ••{getAccountMask(selectedAccount)}</span>}
                                        </p>
                                        {selectedAccount && getStrategyBadge(selectedAccount)}
                                        {selectedAccount && getAccountStatusIndicator(selectedAccount)}
                                    </div>
                                    <div className="flex gap-3 text-xs text-growth/60 mt-0.5 ph-mask">
                                        <span>${selectedAccount?.currentBalance.toLocaleString()}</span>
                                        {selectedAccount?.apr && <span>{selectedAccount.apr}% APR</span>}
                                    </div>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-growth/40 shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {dropdownOpen && (
                                <div className="absolute left-0 right-0 top-full mt-1 bg-cloud rounded-xl shadow-lg border border-mist/20 py-1 z-50 max-h-60 overflow-y-auto">
                                    {accounts.map((account) => (
                                        <button
                                            key={account.liabilityId}
                                            onClick={() => handleSelectAccount(account)}
                                            className={`w-full px-3 py-3 text-left hover:bg-growth/5 transition-colors ${
                                                selectedAccount?.liabilityId === account.liabilityId ? 'bg-foliage/5' : ''
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-medium text-growth truncate ph-mask">
                                                    {account.displayName}
                                                    {getAccountMask(account) && <span className="text-growth/50 font-normal"> ••{getAccountMask(account)}</span>}
                                                </p>
                                                {getStrategyBadge(account)}
                                                {getAccountStatusIndicator(account)}
                                            </div>
                                            <div className="flex gap-3 text-xs text-growth/60 mt-0.5 ph-mask">
                                                <span>${account.currentBalance.toLocaleString()}</span>
                                                {account.apr && <span>{account.apr}% APR</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Strategy hint */}
                        {selectedAccount && selectedAccount.liabilityId === snowballId && (
                            <p className="text-[10px] text-sky mt-1.5 flex items-center gap-1">
                                <Snowflake className="w-3 h-3" />
                                Smallest balance first — build momentum with quick wins
                            </p>
                        )}
                        {selectedAccount && selectedAccount.liabilityId === avalancheId && (
                            <p className="text-[10px] text-clay mt-1.5 flex items-center gap-1">
                                <Flame className="w-3 h-3" />
                                Highest APR first — save the most on interest
                            </p>
                        )}
                    </div>

                    {/* Amount Input */}
                    <div>
                        <div className="flex justify-between items-baseline mb-2">
                            <label className="text-xs font-medium uppercase tracking-wider text-growth/60">Amount</label>
                            {maxPayment > 0 && (
                                <span className="text-[10px] text-growth/40 ph-mask">
                                    Up to ${(Math.floor(maxPayment / 5) * 5).toLocaleString()}
                                </span>
                            )}
                        </div>
                        <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-growth text-2xl font-serif">$</span>
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max={maxPayment > 0 ? maxPayment : undefined}
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => { setAmount(e.target.value); setError(null); }}
                                className="w-full pl-10 pr-4 py-4 bg-cloud border border-mist/20 rounded-xl text-growth text-2xl font-serif text-right focus:outline-none focus:ring-2 focus:ring-foliage/30 focus:border-foliage/50 placeholder:text-growth/20"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Quick amount buttons */}
                    <div className="flex gap-2 flex-wrap">
                        {selectedAccount?.minimumPayment && (
                            <button
                                onClick={() => setAmount(String(selectedAccount.minimumPayment))}
                                className={`ph-mask px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                                    amount === String(selectedAccount.minimumPayment)
                                        ? 'text-cloud bg-foliage'
                                        : 'text-foliage bg-foliage/10 hover:bg-foliage/20'
                                }`}
                            >
                                Min ${selectedAccount.minimumPayment}
                            </button>
                        )}
                        {[25, 50, 100].filter(val => maxPayment <= 0 || val <= maxPayment).map((val) => (
                            <button
                                key={val}
                                onClick={() => setAmount(String(val))}
                                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                                    amount === String(val)
                                        ? 'text-cloud bg-growth'
                                        : 'text-growth/70 bg-growth/5 hover:bg-growth/10'
                                }`}
                            >
                                ${val}
                            </button>
                        ))}
                    </div>

                    {error && <p className="text-xs text-wildfire ph-mask">{error}</p>}

                    {(!amount || parseFloat(amount) <= 0) ? (
                        <button
                            disabled
                            className="w-full border border-mist/30 text-growth/40 py-3.5 rounded-xl font-medium cursor-not-allowed"
                        >
                            Review Payment
                        </button>
                    ) : (
                        <button
                            onClick={handleAmountSubmit}
                            className="w-full bg-growth text-cloud py-3.5 rounded-xl font-medium hover:bg-growth/90 transition-colors"
                        >
                            Review Payment
                        </button>
                    )}

                    <p className="text-[10px] text-growth/40 text-center ph-mask">
                        Payment from {payerInfo.bankName || 'your bank'} ••{payerInfo.bankMask || '****'}
                    </p>
                </>
            )}
        </div>
    );

    const renderCardNumber = () => (
        <div className="space-y-5">
            <div className="text-center py-1">
                <div className="w-12 h-12 rounded-full bg-foliage/10 flex items-center justify-center mx-auto mb-3">
                    <CreditCard className="w-6 h-6 text-foliage" />
                </div>
                <p className="text-xs text-growth/70 mt-1 ph-mask">
                    {selectedAccount?.displayName}{selectedAccount && getAccountMask(selectedAccount) ? ` ••${getAccountMask(selectedAccount)}` : ''} requires your full card number to ensure your payment is processed correctly.
                </p>
                <p className='text-xs text-growth/70 mt-2'>
                    You will only need to enter this once for this account.
                </p>
            </div>

            <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-growth/60 mb-2">Card Number</label>
                <div className="relative">
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder={isAmex(cardNumber) ? '•••• •••••• •••••' : '•••• •••• •••• ••••'}
                        value={showCardNumber ? formatCardDisplay(cardNumber) : cardNumber}
                        onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, isAmex(e.target.value.replace(/\D/g, '')) ? 15 : 16);
                            setCardNumber(digits);
                            setError(null);
                        }}
                        maxLength={showCardNumber ? 19 : 16}
                        style={!showCardNumber && cardNumber.length > 0 ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : undefined}
                        className="w-full px-4 py-4 pr-12 bg-cloud border border-mist/20 rounded-xl text-growth text-lg tracking-widest text-center font-mono focus:outline-none focus:ring-2 focus:ring-foliage/30 focus:border-foliage/50 placeholder:text-growth/20"
                        autoFocus
                        autoComplete="cc-number"
                    />
                    <button
                        type="button"
                        onClick={() => setShowCardNumber(!showCardNumber)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-growth/40 hover:text-growth/70 transition-colors"
                        aria-label={showCardNumber ? 'Hide card number' : 'Show card number'}
                    >
                        {showCardNumber ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Mismatch warning: only show once full card number is entered */}
            {(() => {
                const maskLast4 = selectedAccount ? getAccountMask(selectedAccount) : undefined;
                const expectedLength = isAmex(cardNumber) ? 15 : 16;
                if (cardNumber.length >= expectedLength && maskLast4) {
                    const enteredLast4 = cardNumber.slice(-4);
                    if (enteredLast4 !== maskLast4) {
                        return (
                            <div className="flex items-start gap-2 p-3 bg-foliage/10 border border-foliage/20 rounded-xl">
                                <AlertTriangle className="w-4 h-4 text-foliage shrink-0 mt-0.5" />
                                <p className="text-xs text-growth/70">
                                    The last 4 digits you entered (<span className="font-mono font-medium">{enteredLast4}</span>) don't match the account on file (<span className="font-mono font-medium">{maskLast4}</span>). Please double-check you're entering the right card number.
                                </p>
                            </div>
                        );
                    }
                }
                return null;
            })()}

            {error && <p className="text-xs text-wildfire text-center ph-mask">{error}</p>}

            <div className="flex flex-col items-center gap-1">
                <Lock className="w-3.5 h-3.5 text-growth/40" />
                <p className="text-[10px] text-growth/40 text-center">
                    Your card number is sent securely and is not stored in the app.
                </p>
            </div>

            <button
                onClick={handleCardNumberSubmit}
                disabled={cardNumberSubmitting || cardNumber.length < maxCardLength}
                className={`w-full py-3.5 rounded-xl font-medium transition-colors ${
                    cardNumberSubmitting || cardNumber.length < maxCardLength
                        ? 'border border-mist/30 text-growth/40 cursor-not-allowed'
                        : 'bg-growth text-cloud hover:bg-growth/90'
                }`}
            >
                {cardNumberSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Updating...
                    </span>
                ) : (
                    'Continue'
                )}
            </button>

            <button
                onClick={() => { setStep('amount'); setCardNumber(''); setShowCardNumber(false); setError(null); }}
                className="w-full py-2 text-xs text-growth/50 hover:text-growth/70 transition-colors"
            >
                Choose another account
            </button>
        </div>
    );

    const renderConfirm = () => (
        <div className="space-y-5">
            <button
                onClick={() => setStep('amount')}
                className="flex items-center gap-1 text-xs text-growth/50 hover:text-growth transition-colors"
            >
                <ArrowLeft className="w-3 h-3" />
                Back
            </button>

            <div className="text-center py-2">
                <p className="text-3xl font-serif text-growth ph-mask">${parseFloat(amount).toFixed(2)}</p>
                <p className="text-sm text-growth/70 mt-1 ph-mask">to {selectedAccount?.displayName}{selectedAccount && getAccountMask(selectedAccount) ? ` ••${getAccountMask(selectedAccount)}` : ''}</p>
            </div>

            <div className="p-4 bg-growth/5 rounded-xl space-y-2 text-sm">
                <div className="flex justify-between">
                    <span className="text-growth/60">From</span>
                    <span className="text-growth font-medium ph-mask">{payerInfo.bankName} ••{payerInfo.bankMask}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-growth/60">To</span>
                    <span className="text-growth font-medium ph-mask">{selectedAccount?.displayName}{selectedAccount && getAccountMask(selectedAccount) ? ` ••${getAccountMask(selectedAccount)}` : ''}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-growth/60">Amount</span>
                    <span className="text-growth font-medium ph-mask">${parseFloat(amount).toFixed(2)}</span>
                </div>
            </div>

            <p className="text-[10px] text-growth/40 text-center">
                Payments typically take 3-5 business days to process.
            </p>

            <button
                onClick={handleExecutePayment}
                className="w-full bg-growth text-cloud py-3.5 rounded-xl font-medium hover:bg-growth/90 transition-colors"
            >
                Confirm Payment
            </button>
        </div>
    );

    const renderProcessing = () => (
        <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-foliage mb-3" />
            <p className="text-sm text-growth font-medium">Processing payment...</p>
            <p className="text-xs text-growth/50 mt-1">This may take a moment.</p>
        </div>
    );

    const renderSuccess = () => (
        <div className="text-center py-6 space-y-4">
            <div className="w-14 h-14 rounded-full bg-foliage/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-foliage" />
            </div>
            <div>
                <h3 className="text-lg font-serif text-growth">Payment Submitted</h3>
                <p className="text-sm text-growth/70 mt-1 ph-mask">
                    ${parseFloat(amount).toFixed(2)} to {selectedAccount?.displayName}
                </p>
            </div>
            <p className="text-xs text-growth/50">
                Your payment has been submitted and will be processed within 3-5 business days.
            </p>
            <button
                onClick={handleClose}
                className="w-full bg-growth text-cloud py-3 rounded-xl font-medium hover:bg-growth/90 transition-colors"
            >
                Done
            </button>
        </div>
    );

    const renderError = () => (
        <div className="text-center py-6 space-y-4">
            <div className="w-14 h-14 rounded-full bg-wildfire/10 flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-wildfire" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </div>
            <div>
                <h3 className="text-lg font-serif text-growth">Something went wrong</h3>
                <p className="text-xs text-growth/60 mt-2 max-h-20 overflow-y-auto ph-mask">{error}</p>
            </div>
            <div className="flex gap-2">
                <button
                    onClick={handleClose}
                    className="flex-1 border border-mist/30 text-growth py-3 rounded-xl font-medium hover:bg-growth/5 transition-colors"
                >
                    Close
                </button>
                <button
                    onClick={loadEligibleAccounts}
                    className="flex-1 bg-growth text-cloud py-3 rounded-xl font-medium hover:bg-growth/90 transition-colors"
                >
                    Try Again
                </button>
            </div>
        </div>
    );

    const stepTitles: Record<Step, string> = {
        loading: 'Make a Payment',
        amount: 'Make a Payment',
        cardNumber: 'Add card number',
        confirm: 'Confirm Payment',
        processing: 'Processing',
        success: '',
        error: '',
    };

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={step === 'processing' ? () => {} : handleClose}
            title={stepTitles[step] || undefined}
            showCloseButton={step !== 'processing' && step !== 'success'}
            maxWidth="sm"
        >
            {step === 'loading' && renderLoading()}
            {step === 'amount' && renderAmountInput()}
            {step === 'cardNumber' && renderCardNumber()}
            {step === 'confirm' && renderConfirm()}
            {step === 'processing' && renderProcessing()}
            {step === 'success' && renderSuccess()}
            {step === 'error' && renderError()}
        </ModalWrapper>
    );
};
